/** @jsxImportSource @emotion/react */
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import { types as coreTypes } from '@sitebuilder/core';
import { hooks as commonHooks } from '@sitebuilder/common';
import {
    TextInput,
    SpinnerIcon,
    ErrorMessage,
    PrimaryButton,
    SuccessMessage,
    SecondaryButton,
} from '../index';
import { StateShape as ReduxStateShape } from '../../../reducers';
import { actionCreators } from '../../../reducers/lists';
import { actionCreators as pagesActionCreators } from '../../../reducers/pages';
import { actionCreators as themesActionCreators } from '../../../reducers/themes';
import { actionCreators as componentsActionCreators } from '../../../reducers/components';
import { actionCreators as mediaActionCreators } from '../../../reducers/media';
// eslint-disable-next-line max-len
import { actionCreators as contentListActionCreators } from '../../../reducers/contentList';
import { formIDRegex } from '../../../utils';
import AuthorEmail from '../AuthorEmail';
import { iconTrash, closeIcon } from '../../../assets/images';

interface ItemInfo {
    [key: string]: any;
}

interface ItemInfoProps {
    itemInfo: ItemInfo;
    multipleItemsInfo?: ItemInfo[];
    itemType: string;
    urlState?: string;
}

type FormData = coreTypes.CopyItem;
type DataKeys = 'sourceFileId' | 'destinationFileId';
const idRegex = formIDRegex;

const ItemInformation = ({ itemInfo, itemType, urlState, multipleItemsInfo }: ItemInfoProps) => {
    const i18n = commonHooks.useLocalization();
    const initialCopyFormData = {
        sourceFileId: itemInfo?.name,
        destinationFileId: '',
    };
    const initialCopyFormErrorData = {
        sourceFileId: '',
        destinationFileId: '',
    };
    const initialMoveFormData = {
        sourceFileId: itemInfo?.name,
        destinationFileId: '',
    };
    const initialMoveFormErrorData = {
        sourceFileId: '',
        destinationFileId: '',
    };

    const [formToShow, setFormToShow] = useState('');
    const [copyFormData, setCopyFormData] = useState<FormData>(initialCopyFormData);
    const [moveFormData, setMoveFormData] = useState<FormData>(initialCopyFormData);

    // error messages for invalid inputs
    const [dataError, setDataError] = useState<FormData>(initialCopyFormErrorData);

    // error messages for invalid inputs
    const [moveDataError, setMoveDataError] = useState<FormData>(initialMoveFormErrorData);

    // error message on copy or delete item
    const [error, setError] = useState<string>('');
    const [deleteError, setDeleteError] = useState<string>('');

    // success message on copy or delete item
    const [success, setSuccess] = useState<string>('');
    const [deleteSuccess, setDeleteSuccess] = useState<string>('');
    const [tags, setTags] = useState<string[]>((itemInfo && itemInfo.tags) || []);
    const [addTag, setAddTag] = useState<string>('');
    const {
        listItemCopyError,
        listItemCopyLoading,
        listItemCopySuccess,
        listItemDeleteLoading,
        listItemDeleteSuccess,
        listItemDeleteError,
        listItemMoveLoading,
        listItemMoveSuccess,
        listItemMoveError,
        listItemTagDeleteSuccess,
        listItemTagDeletetag,
        listItemTagAddLoading,
        listItemTagAddSuccess,
        listItemTagAddtag,
    } = useSelector((state: ReduxStateShape) => ({
        ...state?.lists,
    }));
    const dispatch = useDispatch();

    useEffect(() => {
        setCopyFormData(initialCopyFormData);
        setDataError(initialCopyFormErrorData);
        setMoveFormData(initialMoveFormData);
        setMoveDataError(initialMoveFormErrorData);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemInfo?.name]);

    useEffect(() => {
        if (listItemCopySuccess) {
            setSuccess(listItemCopySuccess);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listItemCopySuccess]);

    useEffect(() => {
        if (listItemDeleteSuccess) {
            setDeleteSuccess(listItemDeleteSuccess);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listItemDeleteSuccess]);

    // Update tags on prop change (e.g., if itemInfo changes externally)
    useEffect(() => {
        setTags((itemInfo && itemInfo.tags) || []);
    }, [itemInfo]);

    // When tag deletion succeeds
    useEffect(() => {
        if (listItemTagDeleteSuccess) {
            setTags((prev) => prev.filter((tag) => tag !== listItemTagDeletetag));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listItemTagDeleteSuccess]);

    // When tag addition succeeds
    useEffect(() => {
        if (listItemTagAddSuccess) {
            setTags((prev) => (listItemTagAddtag ? [...prev, listItemTagAddtag] : prev));
            setAddTag('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listItemTagAddSuccess]);

    useEffect(() => {
        if (listItemMoveSuccess) {
            setSuccess(listItemMoveSuccess);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listItemMoveSuccess]);

    useEffect(() => {
        if (listItemCopySuccess) {
            setSuccess(listItemCopySuccess);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listItemCopySuccess]);

    useEffect(() => {
        if (listItemCopySuccess || listItemDeleteSuccess || listItemMoveSuccess) {
            switch (itemType) {
                case 'pages':
                    dispatch(pagesActionCreators.unselectAllPages());
                    dispatch(actionCreators.resetStatusMessages());
                    dispatch(pagesActionCreators.loadPages(urlState || ''));
                    return;
                case 'themes':
                    dispatch(themesActionCreators.unselectAllThemes());
                    dispatch(actionCreators.resetStatusMessages());
                    dispatch(themesActionCreators.listThemes(urlState || ''));
                    return;
                case 'components':
                    dispatch(componentsActionCreators.unselectAllComponents());
                    dispatch(actionCreators.resetStatusMessages());
                    dispatch(componentsActionCreators.listComponents('public', urlState || ''));
                    return;
                case 'media':
                    dispatch(mediaActionCreators.unselectAllMedia());
                    dispatch(actionCreators.resetStatusMessages());
                    dispatch(mediaActionCreators.listMedias(urlState || ''));
                    return;
                case 'contentList':
                    dispatch(contentListActionCreators.unselectAllContentList());
                    dispatch(actionCreators.resetStatusMessages());
                    dispatch(contentListActionCreators.listContentLists(urlState || ''));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listItemCopySuccess, listItemDeleteSuccess, listItemMoveSuccess]);

    useEffect(() => {
        if (listItemCopyError) {
            setError(i18n.t('list_item_copy_error'));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listItemCopyError]);

    useEffect(() => {
        if (listItemDeleteError) {
            setDeleteError(i18n.t('list_item_delete_error'));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listItemDeleteError]);

    useEffect(() => {
        if (listItemMoveError) {
            setError(i18n.t('list_item_move_error'));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listItemMoveError]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, field: DataKeys) => {
        const { value } = e?.target;

        if (error) {
            setError('');
        }

        if (success) {
            setSuccess('');
        }

        if (!value && dataError[field]) {
            setDataError({ ...dataError, [field]: '' });
        }

        if (value.trim()) {
            if (!idRegex.test(value)) {
                setDataError({ ...dataError, [field]: i18n.t('form_invalid_id') });
            } else if (value.length > 32) {
                setDataError({ ...dataError, [field]: i18n.t('form_invalid_id_length') });
            } else {
                setDataError({ ...dataError, [field]: '' });
            }
        } else {
            setDataError({ ...dataError, [field]: i18n.t('form_missing_id') });
        }

        setCopyFormData({
            ...copyFormData,
            [field]: value,
        });
    };

    const handleMoveInputChange = (e: React.ChangeEvent<HTMLInputElement>, field: DataKeys) => {
        const { value } = e?.target;

        if (error) {
            setError('');
        }

        if (success) {
            setSuccess('');
        }

        if (!value && moveDataError[field]) {
            setMoveDataError({ ...moveDataError, [field]: '' });
        }

        if (value.trim()) {
            if (!idRegex.test(value)) {
                setMoveDataError({ ...moveDataError, [field]: i18n.t('form_invalid_id') });
            } else if (value.length > 32) {
                setMoveDataError({ ...moveDataError, [field]: i18n.t('form_invalid_id_length') });
            } else {
                setMoveDataError({ ...moveDataError, [field]: '' });
            }
        } else {
            setMoveDataError({ ...moveDataError, [field]: i18n.t('form_missing_id') });
        }

        setMoveFormData({
            ...moveFormData,
            [field]: value,
        });
    };

    const validateFormData = () => {
        let valid = false;
        const { sourceFileId, destinationFileId } = copyFormData;
        const err: FormData = {
            sourceFileId: '',
            destinationFileId: '',
        };

        if (!sourceFileId) {
            err.sourceFileId = i18n.t('form_missing_source_file_id');
        }

        if (!destinationFileId) {
            err.destinationFileId = i18n.t('form_missing_destination_file_id');
        }

        if (!_.values(err).every(_.isEmpty)) setDataError({ ...dataError, ...err });

        valid =
            _.every(err, (e) => e === '' || _.isNull(e)) &&
            _.every(dataError, (e) => e === '' || _.isNull(e));
        return valid;
    };

    const validateMoveFormData = () => {
        let valid = false;
        const { sourceFileId, destinationFileId } = moveFormData;
        const err: FormData = {
            sourceFileId: '',
            destinationFileId: '',
        };

        if (!sourceFileId) {
            err.sourceFileId = i18n.t('form_missing_source_file_id');
        }

        if (!destinationFileId) {
            err.destinationFileId = i18n.t('form_missing_destination_file_id');
        }

        if (!_.values(err).every(_.isEmpty)) setMoveDataError({ ...moveDataError, ...err });

        valid =
            _.every(err, (e) => e === '' || _.isNull(e)) &&
            _.every(moveDataError, (e) => e === '' || _.isNull(e));
        return valid;
    };

    const handleSubmit = async () => {
        const isFormValid = validateFormData();

        if (isFormValid) {
            dispatch(
                actionCreators.copyListItem(
                    urlState
                        ? `${urlState.substring(1)}/${copyFormData.sourceFileId}`
                        : copyFormData.sourceFileId,
                    urlState
                        ? `${urlState.substring(1)}/${copyFormData.destinationFileId}`
                        : copyFormData.destinationFileId,
                    itemType === 'pages' ? 'reports' : (itemType as coreTypes.listType),
                    'public'
                )
            );

            setCopyFormData(initialCopyFormData);
            setDataError(initialCopyFormErrorData);
        }
    };

    const handleMoveSubmit = async () => {
        const isFormValid = validateMoveFormData();

        if (isFormValid) {
            dispatch(
                actionCreators.moveListItem(
                    urlState
                        ? `${urlState.substring(1)}/${moveFormData.sourceFileId}`
                        : moveFormData.sourceFileId,
                    urlState
                        ? `${urlState.substring(1)}/${moveFormData.destinationFileId}`
                        : moveFormData.destinationFileId,
                    itemType === 'pages' ? 'reports' : (itemType as coreTypes.listType),
                    'public'
                )
            );

            setMoveFormData(initialMoveFormData);
            setMoveDataError(initialMoveFormErrorData);
        }
    };

    const handleDelete = () => {
        dispatch(
            actionCreators.deleteListItems(
                [`${urlState}/${itemInfo?.name}` || itemInfo?.name],
                itemType === 'pages' ? 'reports' : (itemType as coreTypes.listType),
                'public'
            )
        );
    };

    const handleMultipleDelete = () => {
        if (!multipleItemsInfo) return;

        const items = multipleItemsInfo.map((item) => item.name);

        dispatch(
            actionCreators.deleteListItems(
                items,
                itemType === 'pages' ? 'reports' : (itemType as coreTypes.listType),
                'public'
            )
        );
    };

    const handleRemoveTag = (tag: string) => {
        dispatch(
            actionCreators.deleteListTagItem(
                itemInfo?.name,
                tag,
                itemType === 'pages' ? 'reports' : (itemType as coreTypes.listType),
                'public'
            )
        );
    };

    const handleAddTag = () => {
        dispatch(
            actionCreators.addListTagItem(
                itemInfo?.name,
                addTag,
                itemType === 'pages' ? 'reports' : (itemType as coreTypes.listType),
                'public'
            )
        );
    };

    if (multipleItemsInfo && multipleItemsInfo.length > 1) {
        return (
            <>
                <div className="item-information">
                    <div className="item-details">
                        <span className="item-key">Number of items selected</span>
                        <br />
                        <span className="item-value" id="item-name" data-testid="item-name">
                            {multipleItemsInfo.length}
                        </span>
                    </div>
                </div>

                {!itemInfo?.isDeleted && (
                    <>
                        <div className="page-actions-button-container">
                            <SecondaryButton
                                id={'delete-button'}
                                className={'delete-button'}
                                onClick={() =>
                                    formToShow === 'delete'
                                        ? setFormToShow('')
                                        : setFormToShow('delete')
                                }
                            >
                                <img src={iconTrash} alt="icon-trash" />
                            </SecondaryButton>
                        </div>

                        <div className="page-actions-container">
                            {formToShow === 'delete' && (
                                <div className="delete-content" data-testid="delete-content">
                                    <div id={'delete-info'}>
                                        {i18n.t(`${itemType}_delete-warning`)}
                                    </div>
                                    <div className="buttons-group">
                                        {deleteError && <ErrorMessage error={deleteError} />}
                                        {deleteSuccess && (
                                            <SuccessMessage message={deleteSuccess} />
                                        )}
                                        <PrimaryButton
                                            id={'delete-btn'}
                                            className={'delete-btn'}
                                            onClick={handleMultipleDelete}
                                        >
                                            {listItemDeleteLoading && <SpinnerIcon />}
                                            {i18n.t('delete_button')}
                                        </PrimaryButton>
                                        <SecondaryButton
                                            id={'cancel-button'}
                                            className={'cancel-button'}
                                            onClick={() => setFormToShow('')}
                                        >
                                            {i18n.t(`cancel_button`)}
                                        </SecondaryButton>
                                    </div>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </>
        );
    }

    return (
        <>
            <div className="item-information">
                <div className="item-details">
                    <span className="item-key">
                        {itemInfo && itemInfo.headers['ldr-type'] !== undefined
                            ? i18n.t(`folder_name`)
                            : i18n.t(`${itemType}_name_key`)}
                    </span>
                    <br />
                    <span className="item-value" id="item-name" data-testid="item-name">
                        {itemInfo?.name}
                    </span>
                </div>
                <div className="item-details">
                    <span className="item-key">{i18n.t(`${itemType}_author_key`)}</span>
                    <br />
                    <span className="item-value" id="item-author" data-testid="item-author">
                        <AuthorEmail authorId={itemInfo?.author} />
                    </span>
                </div>
                <div className="item-details">
                    <span className="item-key">{i18n.t(`${itemType}_version_key`)}</span>
                    <br />
                    <span className="item-value">{itemInfo?.versionNumber}</span>
                </div>
                <div className="item-details">
                    <span className="item-key">{i18n.t(`status_key`)}</span>
                    <br />
                    <span className="item-value" id={'item-status'} data-testid="item-status">
                        {itemInfo?.isDeleted ? 'Deleted' : 'Active'}
                    </span>
                </div>
                <div className="item-details">
                    <span className="item-key">{i18n.t(`tags_key`)}</span>
                    <br />
                    <span id={'item-tags'} data-testid="item-tags">
                        {tags && tags.length > 0 ? (
                            <div className="item-tags-container">
                                {tags.map((tag: any) => (
                                    <div key={tag} className="item-tags-div">
                                        <span>{tag}</span>
                                        <img
                                            src={closeIcon}
                                            alt="close-trash"
                                            onClick={() => handleRemoveTag(tag)}
                                            className="item-tags-image"
                                        />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <span className="item-value">-</span>
                        )}
                    </span>
                    <div className="add-tag-form">
                        <TextInput
                            className={'form-input'}
                            label={i18n.t('add_tag')}
                            inputId={'tag-input'}
                            labelId={'tag-label'}
                            placeholder={i18n.t('add_tag')}
                            value={addTag}
                            onChange={(e) => setAddTag(e.target.value)}
                        />
                        <div className="add-tags-button-container">
                            <PrimaryButton
                                id={'add-tag-button'}
                                className={'add-tag-button'}
                                onClick={handleAddTag}
                            >
                                {listItemTagAddLoading && <SpinnerIcon />}
                                {i18n.t('add_tag')}
                            </PrimaryButton>
                        </div>
                    </div>
                </div>
                <div className="item-details">
                    <span className="item-key">{i18n.t(`${itemType}_modified_date_key`)}</span>
                    <br />
                    <span className="item-value" data-testid="item-last-modified">
                        {new Date(itemInfo?.lastModified).toLocaleString()}
                    </span>
                </div>
            </div>

            {!itemInfo?.isDeleted && (
                <>
                    <div className="page-actions-button-container">
                        <div className="page-actions-primary-buttons">
                            <PrimaryButton
                                className={'copy-button'}
                                id={'copy-button'}
                                onClick={() =>
                                    formToShow === 'copy'
                                        ? setFormToShow('')
                                        : setFormToShow('copy')
                                }
                            >
                                {itemInfo && itemInfo.headers['ldr-type'] !== undefined
                                    ? i18n.t(`copy_folder`)
                                    : i18n.t(`${itemType}_copy_button`)}
                            </PrimaryButton>
                            <SecondaryButton
                                className={'move-button'}
                                id={'move-button'}
                                onClick={() =>
                                    formToShow === 'move'
                                        ? setFormToShow('')
                                        : setFormToShow('move')
                                }
                            >
                                {itemInfo && itemInfo.headers['ldr-type'] !== undefined
                                    ? i18n.t(`move_folder`)
                                    : i18n.t(`${itemType}_move_button`)}
                            </SecondaryButton>
                        </div>
                        <SecondaryButton
                            id={'delete-button'}
                            className={'delete-button'}
                            onClick={() =>
                                formToShow === 'delete'
                                    ? setFormToShow('')
                                    : setFormToShow('delete')
                            }
                        >
                            <img src={iconTrash} alt="icon-trash" />
                        </SecondaryButton>
                    </div>

                    <div className="page-actions-container">
                        {formToShow === 'move' && (
                            <div className="move-form" data-testid="move-form">
                                <TextInput
                                    className={'form-input'}
                                    label={i18n.t('source_file_id')}
                                    inputId={'move-source-file-input'}
                                    labelId={'move-source-file-label'}
                                    placeholder={i18n.t('source_file_id')}
                                    value={moveFormData.sourceFileId}
                                    onChange={(e) => handleMoveInputChange(e, 'sourceFileId')}
                                    error={moveDataError.sourceFileId}
                                    disabled={true}
                                />
                                <TextInput
                                    className={'form-input'}
                                    label={i18n.t('destination_file_id')}
                                    inputId={'move-desitination-file-input'}
                                    labelId={'move-desitination-file-label'}
                                    placeholder={i18n.t('destination_file_id')}
                                    value={moveFormData.destinationFileId}
                                    onChange={(e) => handleMoveInputChange(e, 'destinationFileId')}
                                    error={moveDataError.destinationFileId}
                                />
                                <div className="buttons-group">
                                    {error && <ErrorMessage error={error} />}
                                    {success && <SuccessMessage message={success} />}
                                    <PrimaryButton
                                        id={'save-button'}
                                        className={'save-button'}
                                        onClick={handleMoveSubmit}
                                    >
                                        {listItemMoveLoading && <SpinnerIcon />}
                                        {i18n.t('Move')}
                                    </PrimaryButton>
                                    <SecondaryButton
                                        id={'cancel-button'}
                                        className={'cancel-button'}
                                        onClick={() => setFormToShow('')}
                                    >
                                        {i18n.t(`cancel_button`)}
                                    </SecondaryButton>
                                </div>
                            </div>
                        )}

                        {formToShow === 'copy' && (
                            <div className="copy-form" data-testid="copy-form">
                                <TextInput
                                    className={'form-input'}
                                    label={i18n.t('source_file_id')}
                                    inputId={'source-file-input'}
                                    labelId={'source-file-label'}
                                    placeholder={i18n.t('source_file_id')}
                                    value={copyFormData.sourceFileId}
                                    onChange={(e) => handleInputChange(e, 'sourceFileId')}
                                    error={dataError.sourceFileId}
                                    disabled={true}
                                />
                                <TextInput
                                    className={'form-input'}
                                    label={i18n.t('destination_file_id')}
                                    inputId={'desitination-file-input'}
                                    labelId={'desitination-file-label'}
                                    placeholder={i18n.t('destination_file_id')}
                                    value={copyFormData.destinationFileId}
                                    onChange={(e) => handleInputChange(e, 'destinationFileId')}
                                    error={dataError.destinationFileId}
                                />
                                <div className="buttons-group">
                                    {error && <ErrorMessage error={error} />}
                                    {success && <SuccessMessage message={success} />}
                                    <PrimaryButton
                                        id={'save-button'}
                                        className={'save-button'}
                                        onClick={handleSubmit}
                                    >
                                        {listItemCopyLoading && <SpinnerIcon />}
                                        {i18n.t('Copy')}
                                    </PrimaryButton>
                                    <SecondaryButton
                                        id={'cancel-button'}
                                        className={'cancel-button'}
                                        onClick={() => setFormToShow('')}
                                    >
                                        {i18n.t(`cancel_button`)}
                                    </SecondaryButton>
                                </div>
                            </div>
                        )}

                        {formToShow === 'delete' && (
                            <div className="delete-content" data-testid="delete-content">
                                <div id={'delete-info'}>{i18n.t(`${itemType}_delete-warning`)}</div>
                                <div className="buttons-group">
                                    {deleteError && <ErrorMessage error={deleteError} />}
                                    {deleteSuccess && <SuccessMessage message={deleteSuccess} />}
                                    <PrimaryButton
                                        id={'delete-btn'}
                                        className={'delete-btn'}
                                        onClick={handleDelete}
                                    >
                                        {listItemDeleteLoading && <SpinnerIcon />}
                                        {i18n.t('delete_button')}
                                    </PrimaryButton>
                                    <SecondaryButton
                                        id={'cancel-button'}
                                        className={'cancel-button'}
                                        onClick={() => setFormToShow('')}
                                    >
                                        {i18n.t(`cancel_button`)}
                                    </SecondaryButton>
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}
        </>
    );
};

export default ItemInformation;
